import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  Box,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  SkipNext as SkipIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Timer as TimerIcon,
  CreditCard as CardIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { debugService, PaymentFlowSession, PaymentFlowStep } from '../services/debugService';

interface PaymentFlowDialogProps {
  open: boolean;
  onClose: () => void;
}

interface DummyCardFormData {
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  cardholderName: string;
}

const PaymentFlowDialog: React.FC<PaymentFlowDialogProps> = ({ open, onClose }) => {
  const [session, setSession] = useState<PaymentFlowSession | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [showEscottIframe, setShowEscottIframe] = useState(false);
  const [escottUrl, setEscottUrl] = useState<string>('');
  const [cardFormData, setCardFormData] = useState<DummyCardFormData>({
    cardNumber: '4111111111111111',
    expiryMonth: '12',
    expiryYear: '2025',
    cvv: '123',
    cardholderName: 'TEST CARDHOLDER'
  });
  const [paymentAmount, setPaymentAmount] = useState(1000);
  const [customerId, setCustomerId] = useState('');

  const initializeSession = async () => {
    try {
      const newSession = await debugService.startPaymentFlowTest(paymentAmount, customerId || undefined);
      setSession(newSession);
      setActiveStep(0);
    } catch (error) {
      console.error('Failed to initialize payment flow session:', error);
    }
  };

  const executeStep = async (stepNumber: number, isAutoMode: boolean = false) => {
    if (!session || isRunning) return;
    
    setIsRunning(true);
    try {
      console.log(`🚀 Executing step ${stepNumber}, isAutoMode: ${isAutoMode}`);
      const updatedSession = await debugService.executePaymentFlowStep(session, stepNumber);
      setSession(updatedSession);
      
      console.log(`📋 Step ${stepNumber} completed. Session state:`, {
        stepNumber,
        isAutoMode,
        step3Status: updatedSession.steps[2]?.status,
        step3Data: updatedSession.steps[2]?.data
      });
      
      // Show appropriate form/iframe after step completion
      if (!isAutoMode) {
        console.log(`🔍 Checking iframe display conditions for step ${stepNumber}`);
        
        // Check for eSCOTT iframe display after step 3 completion
        if (stepNumber === 3 && updatedSession.steps[2].status === 'completed') {
          const step3Data = updatedSession.steps[2].data;
          
          console.log('🔍 Step 3 completed - checking for eSCOTT iframe display:', {
            step3Data,
            action: step3Data?.action,
            redirectUrl: step3Data?.redirectUrl,
            hasEscottAction: step3Data?.action === 'escott_card_registration_redirect'
          });
          
          if (step3Data?.action === 'escott_card_registration_redirect' && step3Data?.redirectUrl) {
            // Real eSCOTT integration - open in new window instead of iframe
            console.log('✅ Opening eSCOTT card registration in new window:', step3Data.redirectUrl);
            
            // Redirect to eSCOTT card registration in same window (E0076 error fix)
            console.log('✅ Redirecting to eSCOTT card registration (same window):', step3Data.redirectUrl);
            
            // Use direct redirect instead of popup to avoid E0076 error
            window.location.href = step3Data.redirectUrl;
          } else {
            console.log('❌ eSCOTT iframe conditions not met:', {
              hasAction: !!step3Data?.action,
              action: step3Data?.action,
              hasRedirectUrl: !!step3Data?.redirectUrl,
              redirectUrl: step3Data?.redirectUrl
            });
          }
        }
        
        // Show simulation form for step 4 if no eSCOTT iframe
        if (stepNumber === 4 && !showEscottIframe) {
          console.log('⚠️ Step 4: No eSCOTT iframe - showing simulation form');
          setShowCardForm(true);
          setShowEscottIframe(false);
        }
        
        // Hide forms when not on interactive steps
        if (stepNumber !== 3 && stepNumber !== 4) {
          setShowCardForm(false);
          setShowEscottIframe(false);
        }
      } else {
        console.log('🤖 Auto mode - skipping iframe/form display logic');
      }
      
      // Auto-advance to next step if current step completed
      if (updatedSession.steps[stepNumber - 1].status === 'completed') {
        setActiveStep(stepNumber);
      }
    } catch (error) {
      console.error(`Failed to execute step ${stepNumber}:`, error);
    } finally {
      setIsRunning(false);
    }
  };

  const executeAllSteps = async () => {
    if (!session || isRunning) return;
    
    setIsRunning(true);
    setIsPaused(false);
    
    for (let i = 1; i <= session.steps.length; i++) {
      if (isPaused) break;
      
      // Execute step in auto mode
      setIsRunning(true);
      try {
        const updatedSession = await debugService.executePaymentFlowStep(session, i);
        setSession(updatedSession);
        
        // Update active step
        if (updatedSession.steps[i - 1].status === 'completed') {
          setActiveStep(i);
        }
        
        // Special handling for eSCOTT card registration step (step 4)
        if (i === 4 && updatedSession.steps[3].status === 'completed') {
          const step3Data = updatedSession.steps[2].data; // Step 3 is token generation (index 2)
          
          // Check if we have a real eSCOTT redirect URL from step 3
          if (step3Data?.redirectUrl && step3Data?.action === 'escott_card_registration_redirect') {
            console.log('Using real eSCOTT URL for card registration:', step3Data.redirectUrl);
            setEscottUrl(step3Data.redirectUrl);
            setShowEscottIframe(true);
            
            // Wait for user interaction or timeout (longer for real eSCOTT)
            await new Promise(resolve => setTimeout(resolve, 15000));
            setShowEscottIframe(false);
          } else {
            // Fallback to simulation form
            console.log('Using simulation card form (no real eSCOTT URL)');
            setShowCardForm(true);
            await new Promise(resolve => setTimeout(resolve, 4000));
            setShowCardForm(false);
          }
        }
        
        // Special handling for payment execution steps (6-7) - show progress
        if ((i === 6 || i === 7) && updatedSession.steps[i - 1].status === 'completed') {
          // Brief pause to show payment processing visualization
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.error(`Failed to execute step ${i}:`, error);
      }
      
      // Wait between steps for better visualization
      if (i < session.steps.length) {
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    }
    
    setIsRunning(false);
  };

  const resetSession = () => {
    setSession(null);
    setActiveStep(0);
    setIsRunning(false);
    setIsPaused(false);
    setShowCardForm(false);
    setShowEscottIframe(false);
    setEscottUrl('');
  };

  const getStepIcon = (step: PaymentFlowStep) => {
    switch (step.status) {
      case 'completed':
        return <SuccessIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'in_progress':
        return <CircularProgress size={20} />;
      default:
        return step.step;
    }
  };

  const getStepStatusColor = (status: PaymentFlowStep['status']) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'in_progress': return 'primary';
      default: return 'default';
    }
  };

  const handleCardFormSubmit = async () => {
    setShowCardForm(false);
    if (session) {
      await executeStep(5, false); // Continue to next step after card form (manual mode)
    }
  };

  const handleEscottComplete = async () => {
    setShowEscottIframe(false);
    setEscottUrl('');
    if (session) {
      await executeStep(5, false); // Continue to next step after eSCOTT registration
    }
  };

  const formatStepData = (data: any) => {
    if (!data) return 'No data';
    return JSON.stringify(data, null, 2);
  };

  useEffect(() => {
    if (open && !session) {
      initializeSession();
    }
  }, [open]);

  const handleClose = () => {
    resetSession();
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">
              決済フロー デバッグテスト
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {/* Setup Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              テスト設定
            </Typography>
            <Box display="flex" gap={2} alignItems="end" mb={2}>
              <TextField
                label="決済金額 (JPY)"
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(Number(e.target.value))}
                disabled={isRunning || Boolean(session)}
                size="small"
                sx={{ width: 150 }}
              />
              <TextField
                label="顧客ID (オプション)"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                disabled={isRunning || Boolean(session)}
                size="small"
                sx={{ width: 200 }}
                placeholder="自動生成されます"
              />
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={resetSession}
                disabled={isRunning}
              >
                リセット
              </Button>
            </Box>
            
            {session && (
              <Box display="flex" gap={2} alignItems="center">
                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  onClick={executeAllSteps}
                  disabled={isRunning || session.status !== 'active'}
                  color="primary"
                >
                  全ステップ自動実行
                </Button>
                
                {isRunning && (
                  <Button
                    variant="outlined"
                    startIcon={<PauseIcon />}
                    onClick={() => setIsPaused(true)}
                  >
                    一時停止
                  </Button>
                )}
                
                <Chip 
                  label={`セッションID: ${session.sessionId}`}
                  size="small"
                  variant="outlined"
                />
                <Chip 
                  label={`ステータス: ${session.status}`}
                  size="small"
                  color={getStepStatusColor(session.status as any)}
                />
              </Box>
            )}
          </Paper>

          {/* Payment Flow Steps */}
          {session && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                決済フロー実行状況
              </Typography>
              
              {/* Progress Indicator */}
              {isRunning && (
                <Box sx={{ mb: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      実行中: {Math.round((activeStep / session.steps.length) * 100)}% 完了
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {activeStep} / {session.steps.length} ステップ
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(activeStep / session.steps.length) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              )}
              
              <Stepper activeStep={activeStep} orientation="vertical">
                {session.steps.map((step, index) => (
                  <Step key={step.step}>
                    <StepLabel
                      icon={getStepIcon(step)}
                      StepIconProps={{
                        style: { 
                          zIndex: 1,
                          color: step.status === 'completed' ? '#4caf50' : 
                                 step.status === 'failed' ? '#f44336' :
                                 step.status === 'in_progress' ? '#2196f3' : '#9e9e9e'
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6">
                          {step.title}
                        </Typography>
                        <Chip 
                          label={step.status}
                          size="small"
                          color={getStepStatusColor(step.status)}
                        />
                        {step.timestamp && (
                          <Typography variant="caption" color="text.secondary">
                            {new Date(step.timestamp).toLocaleTimeString('ja-JP')}
                          </Typography>
                        )}
                      </Box>
                    </StepLabel>
                    
                    <StepContent>
                      <Typography color="text.secondary" paragraph>
                        {step.description}
                      </Typography>
                      
                      {step.status === 'pending' && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PlayIcon />}
                          onClick={() => executeStep(step.step, false)}
                          disabled={isRunning}
                        >
                          このステップを実行
                        </Button>
                      )}
                      
                      {step.data && (
                        <Accordion sx={{ mt: 2 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">
                              実行結果データ
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="body2" fontFamily="monospace" component="pre">
                                {formatStepData(step.data)}
                              </Typography>
                            </Paper>
                          </AccordionDetails>
                        </Accordion>
                      )}
                      
                      <Box sx={{ mt: 1 }} />
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
              
              {session.status === 'completed' && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  <Typography variant="h6">決済フロー完了！</Typography>
                  <Typography>
                    全ての決済ステップが正常に完了しました。
                    実行時間: {session.endTime && new Date(session.endTime).getTime() - new Date(session.startTime).getTime()}ms
                  </Typography>
                </Alert>
              )}
              
              {session.status === 'failed' && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  <Typography variant="h6">決済フロー失敗</Typography>
                  <Typography>
                    決済フローでエラーが発生しました。詳細は各ステップの結果データを確認してください。
                  </Typography>
                </Alert>
              )}
            </Paper>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dummy Card Registration Dialog */}
      <Dialog 
        open={showCardForm} 
        onClose={() => setShowCardForm(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CardIcon color="primary" />
            <Typography variant="h5">
              eSCOTT カード登録画面（シミュレーション）
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 3 }}>
            これは実際のeSCOTTカード登録画面のシミュレーションです。
            実際の決済フローでは、ここでeSCOTTの実際の画面にリダイレクトされます。
            テスト用のダミーデータが事前に入力されています。
          </Alert>
          
          <Box component="form" display="flex" flexDirection="column" gap={3}>
            <TextField
              label="カード番号"
              value={cardFormData.cardNumber}
              onChange={(e) => setCardFormData({...cardFormData, cardNumber: e.target.value})}
              placeholder="1234 5678 9012 3456"
              fullWidth
              InputProps={{
                startAdornment: <CardIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            
            <Box display="flex" gap={2}>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>有効期限（月）</InputLabel>
                <Select
                  value={cardFormData.expiryMonth}
                  onChange={(e) => setCardFormData({...cardFormData, expiryMonth: e.target.value})}
                >
                  {Array.from({length: 12}, (_, i) => (
                    <MenuItem key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {String(i + 1).padStart(2, '0')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>有効期限（年）</InputLabel>
                <Select
                  value={cardFormData.expiryYear}
                  onChange={(e) => setCardFormData({...cardFormData, expiryYear: e.target.value})}
                >
                  {Array.from({length: 10}, (_, i) => (
                    <MenuItem key={2024 + i} value={String(2024 + i)}>
                      {2024 + i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                label="CVV"
                value={cardFormData.cvv}
                onChange={(e) => setCardFormData({...cardFormData, cvv: e.target.value})}
                placeholder="123"
                sx={{ width: 100 }}
              />
            </Box>
            
            <TextField
              label="カード名義人"
              value={cardFormData.cardholderName}
              onChange={(e) => setCardFormData({...cardFormData, cardholderName: e.target.value})}
              placeholder="TARO YAMADA"
              fullWidth
            />
          </Box>
          
          <Divider sx={{ my: 3 }} />
          
          <Box display="flex" alignItems="center" gap={2}>
            <BankIcon color="primary" />
            <Box>
              <Typography variant="subtitle2">
                決済金額: ¥{paymentAmount?.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                テスト決済 - 実際の課金は発生しません
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowCardForm(false)} color="inherit">
            キャンセル
          </Button>
          <Button 
            onClick={handleCardFormSubmit} 
            variant="contained" 
            color="primary"
            startIcon={<ReceiptIcon />}
          >
            カード情報を送信（シミュレート）
          </Button>
        </DialogActions>
      </Dialog>

      {/* eSCOTT Real Integration Dialog */}
      <Dialog 
        open={showEscottIframe} 
        onClose={() => setShowEscottIframe(false)}
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh', maxHeight: '90vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CardIcon color="primary" />
            <Typography variant="h5">
              eSCOTT カード登録画面（実際の画面）
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 2 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>eSCOTTカード登録画面が新しいウィンドウで開きました！</strong>
            <br />
            新しいウィンドウでカード情報を入力して登録を完了してください。
          </Alert>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>手順：</strong>
            <br />
            1. 新しいウィンドウでeSCOTTカード登録画面が表示されます
            <br />
            2. カード番号、有効期限、セキュリティコードを入力
            <br />
            3. 登録完了後、このウィンドウに戻って「登録完了・次へ進む」をクリック
          </Alert>
          
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>eSCOTT URL:</strong> {escottUrl}
            </Typography>
          </Box>
          
          {escottUrl && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button 
                variant="outlined"
                onClick={() => window.location.href = escottUrl}
                startIcon={<CardIcon />}
              >
                eSCOTT画面に移動
              </Button>
            </Box>
          )}
          
          <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              <strong>注意:</strong> これは実際のeSCOTT決済画面です。
              テスト環境では実際の課金は発生しませんが、本番環境では注意してください。
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowEscottIframe(false)} color="inherit">
            キャンセル
          </Button>
          <Button 
            onClick={handleEscottComplete} 
            variant="contained" 
            color="primary"
            startIcon={<SuccessIcon />}
          >
            登録完了・次へ進む
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentFlowDialog;